import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../core/components/atoms/Alert';
import { Button } from '../../../core/components/atoms/Button';
import Container from '../../../core/components/atoms/Container';
import BrainLoadingWrapper from '../../../core/components/molecules/BrainLoading';
import PageHeader from '../../../core/components/molecules/Header';
import InnerHTMLContainer from '../../../core/components/molecules/InnerHTMLContainer';
import { Typography } from '../../../core/components/molecules/Typography';
import { useBoardConfig } from '../../../core/hooks/useBoardConfig';
import { useScreenSize } from '../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../core/hooks/useWindowBreakpoints';
import EssayFooter from '../components/EssayFooter';
import { useCreateEssay } from '../hooks/useCreateEssay';
import { useEssayTheme } from '../hooks/useEssayTheme';
import { useUserEssay } from '../hooks/useUserEssay';
import { useUserEssayStore } from '../stores/essayByUser';

interface IStartEssay {
  isCourse: boolean;
  footerType?: 'embedded' | 'full';
  slug?: string;
  course?: string;
  onClickWrite?: () => void;
  className?: string;
  redirectOnClickWrite?: boolean;
}

export default function StartEssay({
  isCourse,
  footerType = 'full',
  slug,
  course,
  onClickWrite,
  className = '',
  redirectOnClickWrite = true,
}: IStartEssay) {
  const navigate = useNavigate();
  const { data, isPending } = useEssayTheme(slug || '');

  const { board } = useBoardConfig(data?.correctionType || 2);
  const {
    mutate,
    isPending: createLoading,
    isError,
    data: dataCreated,
  } = useCreateEssay();
  const { setEssay } = useUserEssayStore();
  const { data: userEssay } = useUserEssay(slug || '', setEssay);
  const screenSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();

  const [essaySended, setEssaySended] = useState<boolean>(false);

  const removeAlign = (html?: string) => {
    if (html) {
      html = html.replace(
        /<p[^>]*style\s*=\s*['"]?[^'"]*text-align\s*:\s*left[^'"]*['"]?[^>]*>/gi,
        '<p>'
      );
      html = html.replace(
        /<p[^>]*style\s*=\s*['"]?[^'"]*text-align\s*:\s*right[^'"]*['"]?[^>]*>/gi,
        '<p>'
      );

      return html;
    }

    return '';
  };

  const startEssay = () => {
    if (data) {
      mutate({ theme: data?.themeId, moduleOrigin: isCourse ? 'course' : 'essay' });
    }
  };

  const handlePrint = (paperURL: string) => {
    window.open(paperURL);
  };

  useEffect(() => {
    if (!isError && dataCreated && dataCreated.ok) {
      if (isCourse && course) {
        onClickWrite && onClickWrite();
        return;
      }

      if (!redirectOnClickWrite) {
        onClickWrite && onClickWrite();
        return;
      }

      navigate(`/redacoes/escrendo/${slug}`);
    }
  }, [createLoading]);

  useEffect(() => {
    if (userEssay && userEssay.sentCorrection) {
      setEssaySended(true);
    }
  }, [userEssay]);

  const essayTitle = () => {
    if (data && data?.correctionType) {
      switch (data.correctionType) {
        case 2:
          return 'do ENEM';
        case 3:
          return 'da FUVEST';
        case 4:
          return 'da UNESP';
        case 5:
          return 'da UNICAMP';
        case 6:
          return 'da UFPR';
        case 7:
          return 'de Gêneros textuais';
        default:
          return 'do ENEM';
      }
    }

    return '-';
  };

  return (
    <Container
      gap='xxl'
      direction='col'
      className={`xs:mb-[30px] xl:mb-[30px] ${className}`}
    >
      {isCourse && (
        <PageHeader
          title={`Escrever redação ${essayTitle()}`}
          module='Redação'
        />
      )}
      {essaySended && (
        <Alert
          variant='success'
          title='Você já enviou sua redação!'
          message={
            <div>
              <Typography className='my-1'>
                Você encontra suas redações enviadas e corrigidas na área de
                Redação {'>'} Minhas Redações.
              </Typography>
              <Button
                variant='text'
                className='p-0'
                onClick={() => navigate('/redacoes/minhas_redacoes')}
              >
                <Typography size='mini'>Ir para Minhas redações</Typography>
              </Button>
            </div>
          }
        />
      )}
      {isPending ? (
        <BrainLoadingWrapper />
      ) : (
        <div
          className={`m-auto flex flex-col items-center justify-center gap-xxl xs:w-full  xl:pb-[30px] ${
            isCourse ? 'xs:pb-16 md:pb-16 xl:pb-32' : ''
          }`}
        >
          <Typography size='default' weight='bold'>
            {data?.motivationalTitle}
          </Typography>
          <InnerHTMLContainer
            type='essay'
            align='center'
            font='noto'
            data={removeAlign(data?.motivationalText) || ''}
            className='text-mono-color-darkest'
          />
          {!isCourse && footerType === 'full' && (
            <Button
              disabled={essaySended}
              className='rounded-pill px-20'
              onClick={() => onClickWrite && onClickWrite()}
            >
              Enviar redação
            </Button>
          )}

          {!isCourse && footerType === 'embedded' && (
            <div className='flex w-full gap-xl'>
              <Button
                LeftIcon='open_in_new'
                size='small'
                variant='text'
                onClick={() => handlePrint(board.paper)}
                iconSize='small'
              >
                {screenSize >= windowBreakpoints.lg
                  ? 'Imprimir folha de redação'
                  : 'Folha de redação'}
              </Button>
              <div className='flex-1'>
                <Button
                  LeftIcon='edit'
                  size='regular'
                  variant='contained'
                  onClick={() => startEssay()}
                  disabled={essaySended}
                  classNameIcon='hidden lg:flex'
                  full
                >
                  Escrever redação
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {isCourse && (
        <EssayFooter
          onStart={() => startEssay()}
          alreadySended={essaySended}
          paperURL={board.paper}
        />
      )}
    </Container>
  );
}
